import useAcceptReactivationChallenges, {
  AcceptReactivationChallengeResponse,
} from 'features/reactivation/hooks/useAcceptReactivationChallenges';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import { useAuth } from 'shared/providers/auth';
import { isClientError } from 'shared/utils/errors/NetworkErrorClasses';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

interface Challenge {
  challengeId: string;
  challengeType: string;
  challengeState: string;
  challengeContext: string;
}

export interface VerifySsnChallengeData {
  challengeId: string;
  identityId: string;
  acceptOptions: {
    ssnVerifyOption: {
      ssn: string;
    };
  };
}

export interface VerifySsnChallengeResult {
  success: boolean;
  title?: string;
  data?: {
    challengeVerified?: boolean;
    challenges?: Challenge[];
    challengeContext?: string;
    title?: string;
    message?: string;
    errorCode?: number;
  };
  error?: Error;
}

export const INCORRECT_SSN_ERROR_CODE = 6;
export const THROTTLED_ERROR_CODE = 11;

export const FAILURE_MESSAGES = {
  INCORRECT_SSN: 'incorrect_ssn',
  THROTTLED: 'throttled',
  UNKNOWN_ERROR: 'unknown_error',
};

const isAcceptReactivationChallengeResponse = (
  body: unknown,
): body is AcceptReactivationChallengeResponse =>
  typeof body === 'object' && body !== null;

const useVerifySsnChallenge = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const failureReasonRef = useRef<string>('');

  const { idToken, getVaroSessionId } = useAuth();
  const { t: translatedText } = useTranslation('ssn');
  const { postAcceptReactivationChallenge } = useAcceptReactivationChallenges();

  // TODO: Verify the analytics implementation with Van.
  // Refer to ticket JT-135632 for details.

  const logVerificationSsnFailedEvent = usePressableAnalytics({
    analyticsId: 'ssnVerificationFailed',
    customAttributes: {
      get failure_reason() {
        return failureReasonRef.current || errorMessage || '';
      },
      verification_type: 'ssn',
    },
  });

  const logVerificationSsnSuccessfulEvent = usePressableAnalytics({
    analyticsId: 'verificationSuccessful',
    customAttributes: { verification_type: 'ssn' },
  });

  const logVerificationError = (error: Error, failureReason?: string) => {
    failureReasonRef.current = failureReason || '';
    logVerificationSsnFailedEvent();
    failureReasonRef.current = '';
    logBreadcrumb({
      level: 'error',
      message: `Reactivation SSN challenge error: ${error.message}`,
    });
  };

  const verifyChallenge = async (
    data: VerifySsnChallengeData,
  ): Promise<VerifySsnChallengeResult> => {
    setIsLoading(true);
    setErrorMessage(null);
    setIsSuccess(false);

    try {
      const varoSessionId = await getVaroSessionId();
      const headers = await getLambdaHeaders(idToken, varoSessionId);

      const verifyReactivationResult = await postAcceptReactivationChallenge(
        {
          challengeId: data.challengeId,
          identityId: data.identityId,
          acceptOptions: {
            ssnVerifyOption: {
              ssn: data.acceptOptions.ssnVerifyOption.ssn,
            },
          },
        },
        headers,
      );

      const { message, title, errorCode, challengeVerified } =
        verifyReactivationResult;
      const numericErrorCode =
        errorCode !== null && !Number.isNaN(Number(errorCode))
          ? Number(errorCode)
          : undefined;

      if (message) {
        setErrorMessage(message);
      }

      if (numericErrorCode === INCORRECT_SSN_ERROR_CODE) {
        logVerificationError(
          new Error(message || 'Incorrect SSN'),
          FAILURE_MESSAGES.INCORRECT_SSN,
        );

        return {
          success: false,
          title,
          data: {
            ...verifyReactivationResult,
            errorCode: numericErrorCode,
            message,
          },
        };
      }

      if (numericErrorCode === THROTTLED_ERROR_CODE) {
        logVerificationError(
          new Error(message || 'Throttled'),
          FAILURE_MESSAGES.THROTTLED,
        );

        return {
          success: false,
          title,
          data: {
            ...verifyReactivationResult,
            errorCode: numericErrorCode,
            message,
          },
        };
      }

      if (challengeVerified) {
        logVerificationSsnSuccessfulEvent();
        setIsSuccess(true);
        return {
          success: true,
          data: {
            ...verifyReactivationResult,
            errorCode: numericErrorCode,
          },
        };
      }

      logVerificationError(
        new Error(message || 'Unknown error'),
        FAILURE_MESSAGES.UNKNOWN_ERROR,
      );

      return {
        success: false,
        title: title || '',
        data: {
          ...verifyReactivationResult,
          errorCode: numericErrorCode,
          message,
        },
      };
    } catch (error) {
      const typedError =
        error instanceof Error ? error : new Error(String(error));

      const apiErrorData =
        isClientError(error) &&
        isAcceptReactivationChallengeResponse(error.responseBody)
          ? error.responseBody
          : undefined;

      const rawErrorCode = apiErrorData?.errorCode;
      const numericErrorCode =
        rawErrorCode !== undefined && !Number.isNaN(Number(rawErrorCode))
          ? Number(rawErrorCode)
          : undefined;

      const title = apiErrorData?.title;

      logBreadcrumb({
        level: 'error',
        message: `Reactivation SSN challenge error: ${typedError.name}`,
      });

      if (numericErrorCode === INCORRECT_SSN_ERROR_CODE) {
        logVerificationError(
          new Error('Incorrect SSN'),
          FAILURE_MESSAGES.INCORRECT_SSN,
        );
      } else if (numericErrorCode === THROTTLED_ERROR_CODE) {
        logVerificationError(
          new Error('Throttled'),
          FAILURE_MESSAGES.THROTTLED,
        );
      } else {
        logVerificationError(
          new Error('Unknown error'),
          FAILURE_MESSAGES.UNKNOWN_ERROR,
        );
      }

      setErrorMessage(translatedText('genericError'));

      return {
        success: false,
        error: typedError,
        title,
        data: {
          errorCode: numericErrorCode,
          message: translatedText('genericError'),
          title,
        },
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    verifyChallenge,
    isLoading,
    isSuccess,
    errorMessage,
    setErrorMessage,
  };
};

export default useVerifySsnChallenge;
